import { useEmployee } from '@/core/domain/enterprise/Employees/Employees.hooks';
import { useMailConfiguration } from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.hooks';
import { SenderType } from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.types';
import { useMailSend } from '@/core/domain/mail/MailControler/MailControler.hooks';
import {
  ContentType,
  Mail,
  MailMessageType,
  MailSendingForm,
} from '@/core/domain/mail/MailControler/MailControler.types';
import mailBase64ToUrl from '@/utils/functions/mailBase64ToUrl';
import { getUserFromLocalStorage } from '@/utils/user';
import { Alert, Form, Modal, ModalProps } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import MailSendForm from './MailSendForm';
import { MailTypeEnum } from '@/core/domain/mail/MailHistory/MailHistory.types';

type Props = Omit<ModalProps, 'onCancel'> & {
  onCancel: VoidFunction;
  mailData?: Mail;
};

const WorkOrderMailSendModal: React.FC<Props> = ({ mailData, ...props }) => {
  const [form] = Form.useForm<MailSendingForm>();
  const { employeeId, userId } = getUserFromLocalStorage();
  const { data: employee } = useEmployee(Number(employeeId));
  const {
    workorder: { data: workorder },
    company: { data: company },
  } = useWorkorderDetailContext();
  const mailSend = useMailSend();
  const isEdit = !!mailData;
  const [uploading, setUploading] = useState(false);

  const companyName = company?.name || '';

  const { data: mailConfig } = useMailConfiguration({
    sender: SenderType.user,
  });

  useEffect(() => {
    if (mailConfig?.signature && props.open) {
      form.setFieldValue('message', mailConfig.signature);
    }
  }, [mailConfig, props.open]);

  useEffect(() => {
    if (mailData) {
      const { attachments, ...rest } = mailData;
      form.setFieldsValue({
        ...rest,
        attachment: attachments?.length ? attachments[0].content : undefined,
      });
    }
  }, [mailData]);

  async function handleSubmit() {
    let newMessage = '';

    try {
      const { attachment, sendWorkorder, ...values } =
        await form.validateFields();

      if (!employee) return;

      newMessage = await mailBase64ToUrl(values.message, setUploading);

      await mailSend.mutateAsync({
        ...values,
        message: newMessage,
        workorderId: String(workorder?.id),
        mailType: MailTypeEnum.WORKORDER,
        messageType: MailMessageType.Html,
        senderId: String(userId),
        sender: SenderType.user,
        userId: String(userId),
        from: mailConfig?.mail || '',
        fromName: `${companyName} - ${employee?.name}`,
        attachments: sendWorkorder
          ? [
              {
                content: attachment,
                fileName: `orcamento_${moment().format('DD_MM_YYYY')}.pdf`,
                contentType: ContentType.pdf,
              },
            ]
          : undefined,
      });

      handleClose();
    } catch {
      form.setFieldValue('message', newMessage);
    }
  }

  function handleClose() {
    form.resetFields();

    !!props?.onCancel && props.onCancel();
  }

  return (
    <Modal
      {...props}
      width={800}
      title="Novo e-mail"
      cancelText={isEdit ? 'Fechar' : 'Descartar'}
      okText="Enviar"
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={mailSend.isLoading || uploading}
      okButtonProps={{
        style: { display: isEdit ? 'none' : undefined },
        disabled: !mailConfig,
      }}
    >
      {!mailConfig && (
        <Alert
          type="info"
          description="Necessário configurar os dados de e-mail"
          style={{ marginBottom: 24 }}
        />
      )}
      <MailSendForm isEdit={isEdit} form={form} />
    </Modal>
  );
};

export default WorkOrderMailSendModal;
